if (!customElements.get('complementary-bundle')) {
  class ComplementaryBundle extends HTMLElement {
    constructor() {
      super();
      this.onSelectionChange = this.onSelectionChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onCarouselScroll = this.onCarouselScroll.bind(this);
      this.updateCarousel = this.updateCarousel.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.mainCheckbox = this.querySelector('[data-bundle-main-item]');
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.totalElement = this.querySelector('[data-bundle-total]');
      this.errorElement = this.querySelector('[data-bundle-error]');
      this.mainPriceElement = this.querySelector('[data-bundle-main-price]');
      this.mainVariantElement = this.querySelector('[data-bundle-main-variant]');
      this.mainVisual = this.querySelector('[data-bundle-main-visual]');
      this.mainImage = this.mainVisual ? this.mainVisual.querySelector('img') : null;
      this.visuals = this.querySelector('[data-bundle-visuals]');
      this.visualCards = Array.from(this.querySelectorAll('[data-bundle-visual]'));
      this.previousButton = this.querySelector('[data-bundle-previous]');
      this.nextButton = this.querySelector('[data-bundle-next]');
      this.scrollbar = this.querySelector('[data-bundle-scrollbar]');
      this.scrollbarThumb = this.querySelector('[data-bundle-scrollbar-thumb]');

      this.items.forEach(item => item.addEventListener('change', this.onSelectionChange));
      if (this.mainCheckbox) this.mainCheckbox.addEventListener('change', this.onSelectionChange);
      if (this.submitButton) this.submitButton.addEventListener('click', this.onSubmit);
      if (this.previousButton) this.previousButton.addEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);
      if (this.visuals) this.visuals.addEventListener('scroll', this.onCarouselScroll, { passive: true });
      window.addEventListener('resize', this.updateCarousel);
      document.addEventListener('product:variant-change', this.onVariantChange);
      this.updateSelection();
      requestAnimationFrame(this.updateCarousel);
    }

    disconnectedCallback() {
      this.items.forEach(item => item.removeEventListener('change', this.onSelectionChange));
      if (this.mainCheckbox) this.mainCheckbox.removeEventListener('change', this.onSelectionChange);
      if (this.submitButton) this.submitButton.removeEventListener('click', this.onSubmit);
      if (this.previousButton) this.previousButton.removeEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      if (this.visuals) this.visuals.removeEventListener('scroll', this.onCarouselScroll);
      window.removeEventListener('resize', this.updateCarousel);
      document.removeEventListener('product:variant-change', this.onVariantChange);
      if (this.carouselFrame) cancelAnimationFrame(this.carouselFrame);
    }

    onSelectionChange() {
      this.updateSelection();
    }

    onVariantChange(event) {
      if (String(event.detail.sectionId) !== String(this.dataset.sectionId)) return;

      const variant = event.detail.variant;
      if (!variant) {
        this.dataset.mainAvailable = 'false';
        this.updateSelection();
        return;
      }

      const previousVariantId = this.dataset.mainVariantId;
      this.dataset.mainVariantId = variant.id;
      this.dataset.mainPrice = variant.price;
      this.dataset.mainAvailable = variant.available;

      if (this.mainVisual) {
        this.mainVisual.dataset.bundleVisual = variant.id;
      }

      if (this.mainPriceElement) {
        this.mainPriceElement.textContent = this.formatPrice(variant.price);
      }

      if (this.mainVariantElement) {
        const hasVariantTitle = variant.title && variant.title !== 'Default Title';
        this.mainVariantElement.hidden = !hasVariantTitle;
        this.mainVariantElement.textContent = hasVariantTitle ? variant.title : '';
      }

      this.querySelectorAll('[data-bundle-main-link]').forEach(link => {
        link.href = `${this.dataset.mainProductUrl}?variant=${variant.id}`;
      });

      if (variant.featured_image && variant.featured_image.src && this.mainImage) {
        this.mainImage.src = variant.featured_image.src;
        this.mainImage.removeAttribute('srcset');
      }

      if (previousVariantId !== String(variant.id)) this.clearError();
      this.updateSelection();
    }

    updateSelection() {
      let total = 0;
      const mainSelected = this.mainCheckbox && this.mainCheckbox.checked;
      const mainRow = this.mainCheckbox ? this.mainCheckbox.closest('.complementary-bundle__item') : null;

      if (mainRow) mainRow.classList.toggle('is-selected', mainSelected);
      if (this.mainVisual) this.mainVisual.classList.toggle('is-selected', mainSelected);
      if (mainSelected) total += Number(this.dataset.mainPrice) || 0;

      this.items.forEach(item => {
        const selected = item.checked;
        const itemRow = item.closest('.complementary-bundle__item');
        const visual = Array.from(this.querySelectorAll('[data-bundle-visual]')).find(element => element.dataset.bundleVisual === item.dataset.variantId);

        if (itemRow) itemRow.classList.toggle('is-selected', selected);
        if (visual) visual.classList.toggle('is-selected', selected);
        if (selected) total += Number(item.dataset.price) || 0;
      });

      if (this.totalElement) this.totalElement.textContent = this.formatPrice(total);
      if (this.submitButton) {
        const hasSelection = mainSelected || this.items.some(item => item.checked);
        const selectedMainUnavailable = mainSelected && this.dataset.mainAvailable !== 'true';
        this.submitButton.disabled = !hasSelection || selectedMainUnavailable;
      }
    }

    onPrevious() {
      this.scrollByProduct(-1);
    }

    onNext() {
      this.scrollByProduct(1);
    }

    scrollByProduct(direction) {
      if (!this.visuals || !this.visualCards.length) return;

      const viewportLeft = this.visuals.getBoundingClientRect().left;
      const positions = this.visualCards.map(card => this.visuals.scrollLeft + card.getBoundingClientRect().left - viewportLeft);
      const currentIndex = positions.reduce((nearestIndex, position, index) => {
        const nearestDistance = Math.abs(positions[nearestIndex] - this.visuals.scrollLeft);
        return Math.abs(position - this.visuals.scrollLeft) < nearestDistance ? index : nearestIndex;
      }, 0);
      const nextIndex = Math.max(0, Math.min(this.visualCards.length - 1, currentIndex + direction));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.visuals.scrollTo({
        left: positions[nextIndex],
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    onCarouselScroll() {
      if (this.carouselFrame) cancelAnimationFrame(this.carouselFrame);
      this.carouselFrame = requestAnimationFrame(this.updateCarousel);
    }

    updateCarousel() {
      if (!this.visuals) return;

      const maximumScroll = Math.max(0, this.visuals.scrollWidth - this.visuals.clientWidth);
      const currentScroll = Math.max(0, Math.min(maximumScroll, this.visuals.scrollLeft));

      if (this.previousButton) this.previousButton.disabled = currentScroll <= 1;
      if (this.nextButton) this.nextButton.disabled = currentScroll >= maximumScroll - 1;

      if (this.scrollbar && this.scrollbarThumb) {
        const visibleRatio = this.visuals.scrollWidth ? Math.min(1, this.visuals.clientWidth / this.visuals.scrollWidth) : 1;
        const thumbWidth = Math.min(this.scrollbar.clientWidth, Math.max(28, this.scrollbar.clientWidth * visibleRatio));
        const maximumTravel = Math.max(0, this.scrollbar.clientWidth - thumbWidth);
        const progress = maximumScroll ? currentScroll / maximumScroll : 0;

        this.scrollbarThumb.style.width = `${thumbWidth}px`;
        this.scrollbarThumb.style.transform = `translateX(${maximumTravel * progress}px)`;
      }
    }

    formatPrice(cents) {
      if (typeof formatMoney === 'function') {
        return formatMoney(cents, this.dataset.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify && Shopify.currency ? Shopify.currency.active : 'USD'
      }).format(Number(cents) / 100);
    }

    getMainItem() {
      const item = {
        id: Number(this.dataset.mainVariantId),
        quantity: 1
      };
      const form = document.getElementById(`product-form-${this.dataset.sectionId}`);

      if (!form) return item;

      const formData = new FormData(form);
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        if (propertyMatch && typeof value === 'string' && value !== '') {
          properties[propertyMatch[1]] = value;
        }
        if (key === 'selling_plan' && value) item.selling_plan = Number(value);
      }

      if (Object.keys(properties).length) item.properties = properties;
      return item;
    }

    getItems() {
      const selectedItems = [];

      if (this.mainCheckbox && this.mainCheckbox.checked) {
        selectedItems.push(this.getMainItem());
      }

      this.items.filter(item => item.checked).forEach(item => {
        selectedItems.push({
          id: Number(item.dataset.variantId),
          quantity: 1
        });
      });

      return selectedItems;
    }

    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      }, {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      }, {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }

    async onSubmit() {
      if (this.submitButton.disabled || this.submitButton.classList.contains('loading')) return;

      this.clearError();
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('aria-disabled', 'true');

      const sections = this.getSectionsToRender();
      const payload = {
        items: this.getItems(),
        sections: sections.map(section => section.section),
        sections_url: window.location.pathname
      };
      const request = () => fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      try {
        let result;

        if (typeof GiftWithPurchase !== 'undefined' && this.mainCheckbox && this.mainCheckbox.checked) {
          result = await GiftWithPurchase.add({
            addPrimary: request,
            primaryVariantId: this.dataset.mainVariantId,
            quantity: 1,
            giftVariantId: this.dataset.giftVariantId,
            giftAvailable: this.dataset.giftAvailable !== 'false',
            sections: sections.map(section => section.section),
            sectionsUrl: window.location.pathname
          });
        } else {
          const response = await request();
          const state = await response.json();
          result = {
            response: state,
            primaryItem: Array.isArray(state.items) ? state.items[0] : state
          };
          if (!response.ok) throw state;
        }

        if (!result.response || result.response.status) throw result.response;

        this.renderSections(result.response, sections);
        this.openCartDrawer();

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('cart:item-added', { product: result.primaryItem });
        } else {
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: result.primaryItem } }));
        }
      } catch (error) {
        this.showError(error && (error.description || error.message));
      } finally {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    renderSections(state, sections) {
      if (!state.sections) return;

      sections.forEach(section => {
        const destination = document.getElementById(section.id);
        const sectionHtml = state.sections[section.section];
        if (!destination || !sectionHtml) return;

        const parsedDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const source = parsedDocument.querySelector(section.selector) || parsedDocument.querySelector('.shopify-section');
        const elementToReplace = destination.querySelector(section.selector) || destination;
        if (source) elementToReplace.innerHTML = source.innerHTML;

        if (typeof CartDrawer !== 'undefined') new CartDrawer();
        if (typeof Cart !== 'undefined') new Cart().renderContents(state);
      });
    }

    openCartDrawer() {
      const drawer = document.getElementById('Cart-Drawer');
      if (!drawer) return;

      document.body.classList.add('open-cc');
      drawer.classList.add('active');

      if (typeof dispatchCustomEvent === 'function') {
        dispatchCustomEvent('cart-drawer:open');
      } else {
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      }
    }

    clearError() {
      if (!this.errorElement) return;
      this.errorElement.hidden = true;
      this.errorElement.textContent = '';
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message || this.dataset.errorMessage;
      this.errorElement.hidden = false;
      this.errorElement.focus();
    }
  }

  customElements.define('complementary-bundle', ComplementaryBundle);
}
